import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Check, ArrowRight, Sparkles, PenLine, Quote, Calendar } from 'lucide-react';
import ScrollReveal from '../components/ui/ScrollReveal';
import AtlasPlanBriefForm from '../components/concierge/AtlasPlanBriefForm';
import { listPublishedAtlasFiles } from '../api/atlas';
import nelsonPhoto from '../assets/stories/nelson.jpg';
import agathePhoto from '../assets/stories/agathe.jpg';

// Atlas Plan is plan-only: we design the itinerary, the traveller books it
// themselves. Nothing on this page may imply we book flights or hotels.

const TIERS = [
  {
    name: 'Short trip',
    length: 'Up to 4 days',
    price: 89,
    blurb: 'A city break or a long weekend, planned properly.',
  },
  {
    name: 'Full week',
    length: '5 – 8 days',
    price: 179,
    blurb: 'The classic week away — one country, several stops.',
    featured: true,
  },
  {
    name: 'Extended',
    length: '9+ days or multi-country',
    price: 299,
    blurb: 'Longer routes, where the order of the stops is what makes or breaks it.',
  },
];

const STORIES = [
  {
    name: 'Nelson',
    origin: 'Auckland, New Zealand',
    photo: nelsonPhoto,
    quote: 'I tried building my trip myself, even had friends who’d been to Portugal check it over. It still didn’t feel right.',
    detail:
      'I just wanted surf and wind, and a van trip to Portugal was perfect. I had never been to Europe, so thanks to the Atlas Plan, I went to places that really matched what I expected: Peniche, Ericeira, Lisbon and Sagres. Loved it.',
  },
  {
    name: 'Agathe',
    origin: 'Paris, France',
    photo: agathePhoto,
    quote: 'Hawai’i has six islands, I only had two weeks and no idea which ones to actually go to.',
    detail:
      'One conversation settled what two weeks of googling couldn’t: which islands were actually worth my time. Careful research based on other travellers’ experience and at the end what I really wanted to see!',
  },
];

// Swap this for the real Calendly/cal.com booking link once it's set up —
// until then this mailto keeps the CTA working and asks for the same intake info a booking form would.
const CONSULTATION_URL =
  "mailto:alixbsim@gmail.com?subject=Bespoke%20trip%20%E2%80%94%20let's%20talk&body=Destinations%3A%0ADates%2Flength%3A%0AGroup%20size%3A%0AWhat%20makes%20this%20one%20tricky%3A";

const AI_POINTS = ['A plan in minutes', 'Change anything, instantly', 'Free to try'];
const HUMAN_POINTS = ['Checked line by line', 'Told what to cut', 'A real person to ask'];
const CHECKS = ['Day order', 'Travel times', 'Your pace', 'What to cut'];

const HERO_IMAGE =
  'https://images.unsplash.com/photo-1464822759023-fed622ff2c3b?w=1200&q=80&auto=format';

const EXAMPLE_COVER_FALLBACK = [
  'https://images.unsplash.com/photo-1476514525535-07fb3b4ae5f1?auto=format&fit=crop&w=600&q=80',
  'https://images.unsplash.com/photo-1501785888041-af3ef285b470?auto=format&fit=crop&w=600&q=80',
  'https://images.unsplash.com/photo-1508672019048-805c876b67e2?auto=format&fit=crop&w=600&q=80',
];

function PrimaryCta({ children = 'Start your brief', className = '', onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={
        className ||
        'inline-flex items-center gap-2 px-8 py-4 bg-coral-700 hover:bg-coral-800 text-white font-bold rounded-2xl transition-all hover:shadow-lg text-base'
      }
    >
      {children}
      <ArrowRight size={18} />
    </button>
  );
}

export default function AtlasPlanPage() {
  const [briefOpen, setBriefOpen] = useState(false);
  const [examples, setExamples] = useState([]);

  useEffect(() => {
    let cancelled = false;
    listPublishedAtlasFiles({ limit: 3 })
      .then((files) => { if (!cancelled) setExamples(files || []); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  return (
    <div className="bg-[#FAF7F2] min-h-screen">

      <AtlasPlanBriefForm isOpen={briefOpen} onClose={() => setBriefOpen(false)} />

      {/* ── HERO ── */}
      <section className="pt-20 pb-16 px-6 text-center">
        <ScrollReveal>
          <h1 className="text-4xl md:text-5xl font-black text-charcoal-500 mb-5 tracking-tight leading-tight max-w-5xl mx-auto">
            Less time planning. More time travelling.
          </h1>
          <p className="text-lg text-charcoal-400 max-w-xl mx-auto leading-relaxed mb-3">
            We research the details, connect the dots, and build your perfect trip day by day.
          </p>
          <p className="text-sm font-bold text-coral-600 mb-8">
            From €89, fixed price. Planned by a travel professional, not just an app.
          </p>
          <PrimaryCta onClick={() => setBriefOpen(true)} />
        </ScrollReveal>
      </section>

      {/* ── EXAMPLES — see a real itinerary before you brief us ── */}
      {examples.length > 0 && (
        <section className="pb-20 px-6">
          <div className="max-w-5xl mx-auto">
            <ScrollReveal>
              <h2 className="text-2xl md:text-3xl font-black text-charcoal-500 mb-7 text-center leading-snug">
                See what an Atlas Plan actually looks like
              </h2>
            </ScrollReveal>

            <ScrollReveal delay={0.1}>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
                {examples.map((file, i) => (
                  <Link
                    key={file.id}
                    to={`/atlas/${file.id}`}
                    className="group rounded-2xl overflow-hidden bg-white border border-platinum-200 hover:shadow-lg transition-shadow"
                  >
                    <div className="h-36 overflow-hidden">
                      <img
                        src={file.cover_image_url || EXAMPLE_COVER_FALLBACK[i % EXAMPLE_COVER_FALLBACK.length]}
                        alt={file.title}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                        loading="lazy"
                      />
                    </div>
                    <div className="p-4">
                      {file.destination && (
                        <p className="text-xs font-semibold uppercase tracking-wide text-platinum-500 mb-0.5">
                          {file.destination}
                        </p>
                      )}
                      <h3 className="font-bold text-charcoal-500 leading-snug line-clamp-2 mb-1.5">
                        {file.title}
                      </h3>
                      {file.trip_length && (
                        <span className="inline-flex items-center gap-1 text-xs text-charcoal-400">
                          <Calendar size={12} /> {file.trip_length} days
                        </span>
                      )}
                    </div>
                  </Link>
                ))}
              </div>
            </ScrollReveal>

            <ScrollReveal delay={0.15}>
              <p className="text-center mt-6">
                <Link to="/atlas" className="text-sm font-bold text-coral-600 hover:text-coral-700">
                  Browse more itineraries →
                </Link>
              </p>
            </ScrollReveal>
          </div>
        </section>
      )}

      {/* ── SHOWCASE PANEL — AI speed + human judgement ── */}
      <section className="pb-20 px-6">
        <div className="max-w-6xl mx-auto">
          <ScrollReveal>
            <h2 className="text-2xl md:text-4xl font-black text-charcoal-500 mb-4 tracking-tight text-center leading-snug max-w-3xl mx-auto">
              Don't just ask AI. Make sure it's the right trip for you.
            </h2>
            <p className="text-base md:text-lg text-charcoal-400 mb-10 text-center max-w-2xl mx-auto">
              AI can draft an itinerary in seconds. It takes a person who knows the destination to tell you if it's actually worth booking.
            </p>
          </ScrollReveal>

          <ScrollReveal delay={0.1}>
            {/* Wrapper is relative so the two cards can float over the image on lg+ */}
            <div className="relative lg:px-28">

              {/* Centre — the trip */}
              <div className="relative rounded-3xl overflow-hidden aspect-[4/3] sm:aspect-[16/10] lg:aspect-[16/9]">
                <img
                  src={HERO_IMAGE}
                  alt="Mountain landscape"
                  className="absolute inset-0 w-full h-full object-cover"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-charcoal-900/90 via-charcoal-900/65 to-charcoal-900/50" />
                <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-6">
                  <h3 className="text-2xl sm:text-3xl md:text-4xl font-black text-white leading-tight mb-3 drop-shadow">
                    Ten days across<br />northern Spain
                  </h3>
                  <p className="text-sm sm:text-base text-white font-semibold mb-2 drop-shadow">
                    10 days · 2 travellers
                  </p>
                  <p className="text-xs sm:text-sm text-white/90 font-medium drop-shadow">
                    Built with Travel Atlas AI · Checked by a human
                  </p>
                </div>
              </div>

              {/* Floating — AI speed */}
              <div className="mt-4 lg:mt-0 lg:absolute lg:top-10 lg:left-0 lg:w-60 bg-white rounded-2xl border border-platinum-200 shadow-lg p-5">
                <div className="flex items-center gap-2.5 mb-3">
                  <span className="w-8 h-8 rounded-lg bg-columbia-100 flex items-center justify-center flex-shrink-0">
                    <Sparkles size={16} className="text-columbia-700" />
                  </span>
                  <p className="text-sm font-black text-charcoal-500">AI speed</p>
                </div>
                <ul className="space-y-1.5">
                  {AI_POINTS.map((p) => (
                    <li key={p} className="text-sm text-charcoal-400 leading-snug">· {p}</li>
                  ))}
                </ul>
              </div>

              {/* Floating — human expertise */}
              <div className="mt-4 lg:mt-0 lg:absolute lg:bottom-16 lg:right-0 lg:w-60 bg-white rounded-2xl border border-platinum-200 shadow-lg p-5">
                <div className="flex items-center gap-2.5 mb-3">
                  <span className="w-8 h-8 rounded-lg bg-coral-100 flex items-center justify-center flex-shrink-0">
                    <PenLine size={16} className="text-coral-700" />
                  </span>
                  <p className="text-sm font-black text-charcoal-500">Human expertise</p>
                </div>
                <ul className="space-y-1.5">
                  {HUMAN_POINTS.map((p) => (
                    <li key={p} className="text-sm text-charcoal-400 leading-snug">· {p}</li>
                  ))}
                </ul>
              </div>
            </div>
          </ScrollReveal>

          {/* Chips — what a human actually checks */}
          <ScrollReveal delay={0.2}>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-5">
              {CHECKS.map((c) => (
                <div
                  key={c}
                  className="bg-white rounded-xl border border-platinum-200 px-4 py-3 flex items-center gap-2.5"
                >
                  <Check size={15} className="text-coral-600 flex-shrink-0" strokeWidth={3} />
                  <span className="text-sm font-bold text-charcoal-500">{c}</span>
                </div>
              ))}
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* ── HOW IT WORKS + SCOPE ── */}
      <section className="pb-20 px-6">
        <div className="max-w-5xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-4">

          <ScrollReveal>
            <div className="h-full bg-charcoal-500 rounded-2xl p-8">
              <p className="text-xs font-bold uppercase tracking-widest text-coral-300 mb-6">How it works</p>
              <div className="space-y-5">
                {[
                  { n: '01', title: 'Send us your draft', body: 'A Travel Atlas draft, your notes, or just fill in the brief.' },
                  { n: '02', title: 'We build your itinerary', body: 'Back in 2–4 days.' },
                  { n: '03', title: 'You review it', body: 'One revision if the trip is missing something.' },
                ].map(({ n, title, body }) => (
                  <div key={n} className="flex items-start gap-4">
                    <span className="w-7 h-7 rounded-full bg-coral-700 flex items-center justify-center text-white text-[0.65rem] font-black flex-shrink-0 mt-0.5">{n}</span>
                    <div>
                      <p className="text-white font-bold text-[0.95rem]">{title}</p>
                      <p className="text-platinum-200 text-sm mt-1 leading-relaxed">{body}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </ScrollReveal>

          <ScrollReveal delay={0.1}>
            <div className="h-full bg-white rounded-2xl border border-platinum-200 p-8">
              <p className="text-xs font-bold uppercase tracking-widest text-charcoal-400 mb-6">
                What you get
              </p>
              <div className="space-y-3.5">
                {[
                  'A day-by-day itinerary, carefully researched',
                  'Based on your interests, pacing and budget',
                  'Hotel recommendations and links to book',
                  'Timings and routes that actually work',
                  'Activities and experiences you shouldn\'t miss',
                ].map((t) => (
                  <div key={t} className="flex items-start gap-3">
                    <Check size={16} className="text-coral-600 flex-shrink-0 mt-0.5" strokeWidth={3} />
                    <span className="text-[0.95rem] text-charcoal-500 leading-snug">{t}</span>
                  </div>
                ))}
              </div>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* ── PRICING ── */}
      <section className="pb-20 px-6">
        <div className="max-w-5xl mx-auto">
          <ScrollReveal>
            <h2 className="text-3xl md:text-4xl font-black text-charcoal-500 mb-9 tracking-tight text-center">
              Fixed prices
            </h2>
          </ScrollReveal>

          <ScrollReveal>
            <p className="text-[0.95rem] text-charcoal-400 leading-relaxed max-w-lg mx-auto text-center mb-9">
              One fee, built from nothing. Price follows the length of the trip.
            </p>
          </ScrollReveal>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {TIERS.map(({ name, length, price, blurb, featured }, i) => (
              <ScrollReveal key={name} delay={i * 0.07}>
                <div
                  className={`h-full rounded-2xl p-7 flex flex-col ${
                    featured
                      ? 'bg-charcoal-500 ring-2 ring-coral-400'
                      : 'bg-white border border-platinum-200'
                  }`}
                >
                  <div className="flex items-center justify-between gap-2 mb-1">
                    <p className={`text-base font-black ${featured ? 'text-white' : 'text-charcoal-500'}`}>
                      {name}
                    </p>
                    {featured && (
                      <span className="text-[0.6rem] font-black uppercase tracking-widest text-charcoal-500 bg-coral-300 px-2 py-1 rounded-full">
                        Popular
                      </span>
                    )}
                  </div>
                  <p className={`text-xs font-semibold mb-5 ${featured ? 'text-platinum-300' : 'text-charcoal-400'}`}>
                    {length}
                  </p>
                  <p className={`text-4xl font-black leading-none mb-5 ${featured ? 'text-white' : 'text-charcoal-500'}`}>
                    €{price}
                  </p>
                  <p className={`text-sm leading-relaxed flex-1 ${featured ? 'text-platinum-200' : 'text-charcoal-400'}`}>
                    {blurb}
                  </p>
                </div>
              </ScrollReveal>
            ))}
          </div>

          <ScrollReveal delay={0.25}>
            <div className="mt-6 rounded-2xl border border-dashed border-platinum-300 bg-white/60 px-6 py-6 text-center max-w-2xl mx-auto">
              <p className="text-sm font-bold text-charcoal-500 mb-1">
                Multi-country, a group with split plans, or want to talk it through first?
              </p>
              <p className="text-xs text-charcoal-400 mb-4">
                Book a free 30-minute call. Bespoke itineraries from €350.
              </p>
              <a
                href={CONSULTATION_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-6 py-3 bg-white border-2 border-coral-600 text-coral-700 font-bold rounded-xl hover:bg-coral-50 transition-colors text-sm"
              >
                Book a call
                <ArrowRight size={16} />
              </a>
            </div>
          </ScrollReveal>

        </div>
      </section>

      {/* ── SUCCESS STORIES ── */}
      <section className="pb-24 px-6">
        <div className="max-w-5xl mx-auto">
          <ScrollReveal>
            <h2 className="text-2xl md:text-3xl font-black text-charcoal-500 mb-7 text-center leading-snug">
              Real trips, planned
            </h2>
          </ScrollReveal>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {STORIES.map(({ name, origin, photo, quote, detail }, i) => (
              <ScrollReveal key={name} delay={i * 0.1}>
                <div className="h-full bg-white rounded-2xl border border-platinum-200 p-8 flex flex-col">
                  <div className="flex items-center gap-3 mb-5">
                    <img
                      src={photo}
                      alt={name}
                      className="w-16 h-16 rounded-full object-cover flex-shrink-0"
                      loading="lazy"
                    />
                    <Quote size={20} className="text-coral-400" strokeWidth={2.5} />
                  </div>
                  <p className="text-lg font-black text-charcoal-500 leading-snug mb-4">
                    “{quote}”
                  </p>
                  <p className="text-sm text-charcoal-400 leading-relaxed flex-1 mb-5">
                    {detail}
                  </p>
                  <p className="text-xs font-bold uppercase tracking-wider text-coral-600">
                    {name}, {origin}
                  </p>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

    </div>
  );
}
